<template>
  <div>
    <p>
      <strong>{{ tn("autoRenewForLine1") }}</strong>
    </p>
    <ul class="tight mt mb">
      <li>
        <em>{{ tn("subscription") }}</em>
        {{ subscriptionName }}
      </li>
      <li>
        <em>{{ tn("computer") }}</em>
        {{ computerName }}
      </li>
      <li>
        <em>{{ tn("expires") }}</em>
        {{ formatExpirationDate(expiresOn) }}
      </li>
    </ul>
    <div v-if="showPaymentMethod">
      <p>{{ tn("creditCardInfo") }}</p>
      {{ creditCardType }} *{{ getLastFourDigitsCreditCard(creditCardLastDigits) }} (Exp.
      {{ formatCompactExpirationDate(expiresOn) }})
      <button id="changePaymentMethod" type="button" class="inline-btn-link" @click="changePaymentMethod()">
        {{ tn("changePaymentLink") }}
      </button>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, ref, computed } from "vue";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { logEvent } from "@/common/logger";
import { formatDateString, formatCCDateString } from "@/common/dateFormat";
import { t } from "@/i18n";

const componentName = "AutoRenewTurnOnComponent";

export default defineComponent({
  name: componentName,
  props: {
    subscription: {
      type: Object as PropType<ISubscription>,
      required: true,
    },
  },
  emits: ["change-payment-method"],

  setup(props, context) {
    logEvent("created", componentName, props.subscription);

    const subscriptionName = ref<string>(props.subscription?.productDisplayName ?? "");
    const expiresOn = ref<string>(props.subscription?.expirationDate ?? "");
    const creditCardType = ref<string>(props.subscription?.paymentInfo?.cardType ?? "");
    const creditCardLastDigits = ref<string>(props.subscription?.paymentInfo?.cardLastFourDigits ?? "");
    const showPaymentMethod = ref<boolean>(false);

    if (creditCardType.value) {
      showPaymentMethod.value = true;
    }

    const computerName = computed(() => {
      return props.subscription?.computer?.description ?? props.subscription?.computer?.name ?? "";
    });

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    function changePaymentMethod() {
      context.emit("change-payment-method");
    }

    function formatExpirationDate(expirationDate: string) {
      return formatDateString(expirationDate);
    }

    function formatCompactExpirationDate(expirationDate: string) {
      return formatCCDateString(expirationDate);
    }

    function getLastFourDigitsCreditCard(lastDigits: string) {
      return lastDigits.substring(lastDigits.length - 4, lastDigits.length);
    }

    //Expose to template
    return {
      tn,
      subscriptionName,
      computerName,
      expiresOn,
      creditCardType,
      creditCardLastDigits,
      showPaymentMethod,
      changePaymentMethod,
      formatExpirationDate,
      formatCompactExpirationDate,
      getLastFourDigitsCreditCard,
    };
  },
});
</script>
<style scoped lang="css">
@import "@/styles/variables.css";
</style>
